/**
 * Custom hook for tag management operations
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export const useTagManagement = () => {
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [batches, setBatches] = useState([]);
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [dailyViews, setDailyViews] = useState([]);
  const [batchStats, setBatchStats] = useState({});

  // Load all tags
  const loadTags = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading tags from:', `${API}/admin/tags`);
      const response = await axios.get(`${API}/admin/tags`);
      console.log('Tags loaded:', response.data?.tags?.length || 0);
      setTags(response.data.tags || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Load tags error:', error.message, error.code);
      // Better error messages based on error type
      if (error.code === 'ERR_NETWORK') {
        toast.error('Connection failed. Check your internet connection.');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Server may be busy.');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. You may not have permission.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error.response?.status === 503) {
        toast.error('Service unavailable. Server is under maintenance.');
      } else {
        toast.error(`Failed to load tags: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load batches
  const loadBatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/batches`);
      setBatches(response.data.batches);
    } catch (error) {
      console.error('Failed to load batches:', error);
    }
  }, []);

  // Load batch stats
  const loadBatchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/batch-stats`);
      const statsMap = {};
      response.data.batches.forEach(b => {
        statsMap[b.name] = b;
      });
      setBatchStats(statsMap);
    } catch (error) {
      console.error('Failed to load batch stats:', error);
    }
  }, []);

  // Load analytics stats
  const loadAnalyticsStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics-summary`);
      setAnalyticsStats(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }, []);

  // Load daily views
  const loadDailyViews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics-daily`);
      setDailyViews(response.data.daily_views);
    } catch (error) {
      console.error('Failed to load daily views:', error);
    }
  }, []);

  // Reset a single tag
  const resetTag = useCallback(async (nfcCode) => {
    try {
      await axios.delete(`${API}/tags/${nfcCode}/reset`);
      toast.success('Tag reset successfully');
      loadTags();
      return true;
    } catch (error) {
      toast.error('Failed to reset tag');
      return false;
    }
  }, [loadTags]);

  // Delete a single tag
  const deleteTag = useCallback(async (nfcCode) => {
    try {
      await axios.delete(`${API}/admin/tags/${nfcCode}`);
      toast.success('Tag deleted successfully');
      loadTags();
      return true;
    } catch (error) {
      toast.error('Failed to delete tag');
      return false;
    }
  }, [loadTags]);

  // Delete multiple tags
  const deleteMultipleTags = useCallback(async (nfcCodes) => {
    try {
      const response = await axios.post(`${API}/admin/delete-by-codes`, {
        nfc_codes: nfcCodes
      });
      toast.success(`Deleted ${response.data.deleted_count} tags`);
      setSelectedTags([]);
      loadTags();
      return true;
    } catch (error) {
      toast.error('Failed to delete some tags');
      loadTags();
      return false;
    }
  }, [loadTags]);

  // Bulk reset tags
  const bulkResetTags = useCallback(async (nfcCodes) => {
    let resetCount = 0;
    for (const nfcCode of nfcCodes) {
      try {
        await axios.delete(`${API}/tags/${nfcCode}/reset`);
        resetCount++;
      } catch (error) {
        console.error(`Error resetting tag ${nfcCode}:`, error);
      }
    }
    toast.success(`Reset ${resetCount} tags successfully`);
    setSelectedTags([]);
    loadTags();
    return resetCount;
  }, [loadTags]);

  // Move tag to folder (uses folder_id, not batch)
  const moveTagToFolder = useCallback(async (nfcCode, folderId) => {
    try {
      await axios.post(`${API}/admin/tags/move`, {
        tag_ids: [nfcCode],
        folder_id: folderId  // null = unorganized
      });
      return true;
    } catch (error) {
      console.error(`Error moving tag ${nfcCode}:`, error);
      return false;
    }
  }, []);

  // Move tag to batch (legacy)
  const moveTag = useCallback(async (nfcCode, targetBatch) => {
    try {
      await axios.post(`${API}/admin/move-tag`, {
        nfc_code: nfcCode,
        target_batch: targetBatch
      });
      return true;
    } catch (error) {
      console.error(`Error moving tag ${nfcCode}:`, error);
      return false;
    }
  }, []);

  // Bulk move tags to folder (uses folder_id)
  const bulkMoveToFolder = useCallback(async (nfcCodes, targetFolderId) => {
    try {
      const response = await axios.post(`${API}/admin/tags/move`, {
        tag_ids: nfcCodes,
        folder_id: targetFolderId  // null = unorganized
      });
      const folderName = targetFolderId ? 'folder' : 'unorganized';
      toast.success(response.data.message || `Moved ${nfcCodes.length} tags to ${folderName}`);
      setSelectedTags([]);
      loadTags();
      return response.data.modified_count;
    } catch (error) {
      toast.error('Failed to move tags');
      return 0;
    }
  }, [loadTags]);

  // Download QR code with optional styling
  const downloadQRCode = useCallback(async (nfcCode, options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.fgColor) params.append('fg_color', options.fgColor.replace('#', ''));
      if (options.bgColor) params.append('bg_color', options.bgColor.replace('#', ''));
      if (options.size) params.append('size', options.size);
      if (options.logoUrl) params.append('logo_url', options.logoUrl);
      if (options.dotStyle) params.append('dot_style', options.dotStyle);
      
      const queryString = params.toString();
      const url = `${API}/admin/qr-code/${nfcCode}${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `${nfcCode}-qr.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  }, []);

  // Bulk download QR codes with optional styling
  const bulkDownloadQRCodes = useCallback(async (nfcCodes, options = {}) => {
    toast.info(`Downloading ${nfcCodes.length} QR codes...`);
    for (const nfcCode of nfcCodes) {
      await downloadQRCode(nfcCode, options);
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    toast.success(`Downloaded ${nfcCodes.length} QR codes`);
  }, [downloadQRCode]);

  // Delete batch
  const deleteBatch = useCallback(async (batchName) => {
    if (!window.confirm(`Delete batch "${batchName}" and all its tags?`)) return;
    try {
      await axios.delete(`${API}/admin/batch/${batchName}`);
      toast.success(`Batch "${batchName}" deleted`);
      loadTags();
      loadBatches();
    } catch (error) {
      toast.error('Failed to delete batch');
    }
  }, [loadTags, loadBatches]);

  // Toggle tag selection
  const toggleSelectTag = useCallback((nfcCode) => {
    setSelectedTags(prev =>
      prev.includes(nfcCode)
        ? prev.filter(id => id !== nfcCode)
        : [...prev, nfcCode]
    );
  }, []);

  // Toggle select all
  const toggleSelectAll = useCallback((filteredTags) => {
    if (selectedTags.length === filteredTags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags(filteredTags.map(t => t.nfc_code));
    }
  }, [selectedTags.length]);

  // Copy to clipboard
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }, []);

  return {
    // State
    tags,
    stats,
    loading,
    selectedTags,
    batches,
    analyticsStats,
    dailyViews,
    batchStats,
    
    // Setters
    setSelectedTags,
    
    // Loaders
    loadTags,
    loadBatches,
    loadBatchStats,
    loadAnalyticsStats,
    loadDailyViews,
    
    // Actions
    resetTag,
    deleteTag,
    deleteMultipleTags,
    bulkResetTags,
    moveTag,
    moveTagToFolder,
    bulkMoveToFolder,
    downloadQRCode,
    bulkDownloadQRCodes,
    deleteBatch,
    toggleSelectTag,
    toggleSelectAll,
    copyToClipboard,
  };
};

export default useTagManagement;
